import { dueNowResponseSchema, tasksResponseSchema, type DueNowResponse, type TasksResponse } from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, type QueryClient } from "@tanstack/react-query";
import { apiRequest, ApiRequestError } from "../api/http";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { taskKeys } from "./task-keys";
import { tasksListQueryOf, type TasksListQuery } from "./task-search";

// Le letture di «Le cose da fare» (piano M5, Task 18): `GET /api/tasks` a pagine, con la scheda, «Di chi», «Cosa» e l'ordine
// dell'indirizzo. La prima pagina di «Oggi» la legge in anticipo la voce «Task» della rail, come le altre voci, così la pagina
// si apre subito; una chiamata finita o un richiamo fissato la fanno rileggere (`taskKeys.all`).

export const TASKS_PAGE_SIZE = 50;

/** Ogni quanto il guscio chiede i richiami concordati con l'ora arrivata (D14: un sondaggio, la campanella viene dopo). */
export const DUE_NOW_REFRESH_MS = 60_000;

/** La vista di partenza dell'indirizzo `/task` senza parametri: «Oggi», tutto il team (per chi lo vede), come vanno fatti. */
export const DEFAULT_TASKS_QUERY: TasksListQuery = tasksListQueryOf({}, false);

function tasksPath(query: TasksListQuery, offset: number): string {
  const params = new URLSearchParams({ view: query.view });
  if (query.who !== undefined) params.set("who", query.who);
  if (query.kind !== "all") params.set("kind", query.kind);
  if (query.sort !== "due") params.set("sort", query.sort);
  params.set("offset", String(offset));
  params.set("limit", String(TASKS_PAGE_SIZE));
  return `/api/tasks?${params.toString()}`;
}

/** Le pagine dell'agenda: la successiva finché le righe lette sono meno del totale della scheda. */
export function tasksPagesQuery(tab: TabContext, query: TasksListQuery, view: string | null) {
  return infiniteQueryOptions({
    queryKey: taskKeys.list(tabKey(tab), view, query),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", tasksPath(query, pageParam), { schema: tasksResponseSchema, tab, signal }),
    initialPageParam: 0,
    getNextPageParam: (last: TasksResponse) => (last.rows.length > 0 && last.offset + last.rows.length < last.total ? last.offset + last.rows.length : undefined),
    // Tornare ai Task entro mezzo minuto li mostra subito; una chiamata o un richiamo li fanno rileggere da sé.
    staleTime: 30_000,
    // Scadenze e conteggi li calcola il server: con l'agenda aperta, ogni minuto un richiamo che scade diventa rosso e i numeri
    // seguono, anche al cambio di giorno dell'azienda (rilievo di Codex sulla #102). Con la pagina nascosta non si rilegge.
    refetchInterval: DUE_NOW_REFRESH_MS,
  });
}

/**
 * Legge in anticipo la prima pagina di «Oggi» (velocità: criterio di accettazione), per la voce della rail: la chiave è quella
 * della pagina aperta senza parametri, nella Vista Commerciale della scheda se c'è. Non rilegge una pagina ancora fresca.
 */
export function prefetchTasks(queryClient: QueryClient, tab: TabContext): Promise<void> {
  return queryClient.prefetchInfiniteQuery(tasksPagesQuery(tab, DEFAULT_TASKS_QUERY, commercialViewOf(tab)));
}

/** Dopo una chiamata o un richiamo fissato, spostato o fatto: l'agenda della scheda si rilegge, in ogni vista. */
export function refreshTasks(queryClient: QueryClient, tab: TabContext): void {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all(tabKey(tab)) });
}

/**
 * `GET /api/tasks/due-now` (D14), per il banner del guscio: ogni 60 secondi e a ogni ritorno sulla finestra, nella vista della
 * scheda (nella «Guarda come…» i richiami della persona osservata, questione 320). Sta sotto `taskKeys.all`: la fine di una
 * chiamata (`refreshAfterCall`) e un richiamo fissato, spostato o fatto lo fanno rileggere, così il banner sparisce appena il
 * richiamo è completato. Un server senza la rotta (la demo locale di prima di M5) risponde il `404 not_found` di ogni rotta che
 * non c'è: nessun banner, senza errore.
 */
export function dueNowQuery(tab: TabContext) {
  return queryOptions({
    queryKey: taskKeys.dueNow(tabKey(tab), commercialViewOf(tab)),
    queryFn: async ({ signal }): Promise<DueNowResponse> => {
      try {
        return await apiRequest("GET", "/api/tasks/due-now", { schema: dueNowResponseSchema, tab, signal });
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404 && error.code === "not_found") return { tasks: [] };
        throw error;
      }
    },
    refetchInterval: DUE_NOW_REFRESH_MS,
  });
}
